import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";

/**
 * Strip any leading "Dr"/"Dr."/"dr"/"DR" + trailing space/dot so what
 * lives in form state is just the doctor's actual name. The UI shows a
 * sticky "Dr." chip so the title isn't a typing task.
 *
 * Regex contract: only strip when there's a name character AFTER the
 * title. This avoids the incremental-typing glitch where a user typing
 * "D" → "Dr" → "." sees the field cleared too early — they pause, look
 * at the chip, and realise they don't need to type it. The strip
 * happens the moment they hit the first letter of their name.
 *   - "D"             → "D"
 *   - "Dr"            → "Dr"
 *   - "Dr."           → "Dr."
 *   - "Dr. "          → "Dr. "
 *   - "Dr. K"         → "K"
 *   - "Dr Anita Rao"  → "Anita Rao"
 *   - "Anita Rao"     → "Anita Rao"  (no-op)
 */
export function stripDrPrefix(raw) {
    if (!raw) return "";
    return String(raw).replace(/^\s*dr\.?\s+(?=\S)/i, "");
}

/**
 * DoctorNameInput — name field with a sticky "Dr." chip on the left.
 * Mirrors the +91 chip on PhoneInput10. Auto-strips any "Dr"/"dr" the
 * user types in (forgiving), so the chip is never duplicated.
 *
 * State contract: parent stores just the bare name (no "Dr." prefix).
 * Backend `build_doctor_from_application` already prefixes "Dr." when
 * the applicant's name doesn't start with it.
 */
export default function DoctorNameInput({
    value,
    onChange,
    placeholder = "Full name",
    locked = false,
    disabled = false,
    className = "",
    inputClassName = "",
    testid,
    autoFocus = false,
    id,
    name,
}) {
    const displayValue = useMemo(() => stripDrPrefix(value), [value]);

    const handleChange = (e) => {
        // Forgiving: strip "Dr" prefix if the user typed it anyway.
        const cleaned = stripDrPrefix(e.target.value);
        onChange?.(cleaned);
    };

    return (
        <div className={`relative flex items-stretch ${className}`}>
            <span
                className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-secondary/60 text-sm font-mono font-medium text-muted-foreground select-none"
                aria-hidden="true"
            >
                Dr.
            </span>
            <Input
                id={id}
                name={name}
                type="text"
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                readOnly={locked}
                disabled={disabled || locked}
                autoFocus={autoFocus}
                className={`rounded-l-none flex-1 ${inputClassName}`}
                data-testid={testid}
                aria-label="Doctor name (without title)"
            />
        </div>
    );
}
